import { type ComponentProps } from "@ts/components";
import React, { type FC, ChangeEvent } from "react";
import { ErrorMap } from "./withReviewsForm";
import { useRouteLoaderData } from "@hooks/useRouteLoaderData";

type FitFeedbackInputProps = ComponentProps & {
  errors: ErrorMap;
  fitLength: string;
  fitWidth: string;
  gender: "Womens" | "Mens";
  handleFitLengthChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleFitWidthChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSizeChange: (value: any) => void;
  handleTitleChange: ({ target: { name, value } }) => void;
  title: string;
};

type FitFeedbackOutputProps = FitFeedbackInputProps & {
  shoeFitLength: {
    value: string;
    label: string;
  }[];
  shoeFitWidth: {
    value: string;
    label: string;
  }[];
  sizeOptions?: {
    id: string;
    label: string;
    value: string;
    available: boolean;
  }[];
};

export const shoeFitLength = [
  {
    value: "1",
    label: "Too short",
  },
  {
    value: "3",
    label: "Perfect",
  },
  {
    value: "5",
    label: "Too long",
  },
];

export const shoeFitWidth = [
  {
    value: "1",
    label: "Too narrow",
  },
  {
    value: "3",
    label: "Perfect",
  },
  {
    value: "5",
    label: "Too wide",
  },
];

export const withFitFeedback =
  (Component: FC<FitFeedbackOutputProps>) =>
  ({
    name = "FitFeedback",
    errors,
    gender,
    fitLength,
    fitWidth,
    title,
    handleTitleChange,
    handleSizeChange,
    handleFitWidthChange,
    handleFitLengthChange,
  }: FitFeedbackInputProps) => {
    const { sizeGuide } = useRouteLoaderData("routes/$site");
    const { men, women } = sizeGuide;

    const formatWomenSize = women?.map((size) => {
      return {
        id: size._key,
        label: `${size.bared} (US ${size.us_au})`,
        value: `${size.bared} (US ${size.us_au})`,
        available: true,
      };
    });
    const formatMenSize = men?.map((size) => {
      return {
        id: size._key,
        label: `${size.bared} (US ${size.us_au})`,
        value: `${size.bared} (US ${size.us_au})`,
        available: true,
      };
    });
    const sizeOptions = gender === "Womens" ? formatWomenSize : formatMenSize;

    Component.displayName = name;
    return (
      <Component
        errors={errors}
        fitLength={fitLength}
        fitWidth={fitWidth}
        gender={gender}
        handleFitWidthChange={handleFitWidthChange}
        handleFitLengthChange={handleFitLengthChange}
        handleSizeChange={handleSizeChange}
        handleTitleChange={handleTitleChange}
        sizeOptions={sizeOptions}
        shoeFitLength={shoeFitLength}
        shoeFitWidth={shoeFitWidth}
        title={title}
      />
    );
  };
